import { useState } from "react";
import { useAxios } from "common";
import { Card, CollapsibleHeader } from "common";
import styled from "styled-components";

const Container = styled.div`
  margin-top: 20px;
`;

const LogEntry = styled.div`
  padding: 12px;
  border-bottom: 1px solid #eee;
  
  &:last-child {
    border-bottom: none;
  }
`;

const LogHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

const LogUser = styled.span`
  font-weight: 600;
  color: #333;
`;

const LogTimestamp = styled.span`
  font-size: 12px;
  color: #666;
`;

const LogDescription = styled.div`
  color: #555;
  margin-bottom: 8px;
`;

const ChangedFields = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 8px;
`;

const FieldTag = styled.span`
  background: #f0f0f0;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  color: #555;
`;

const ChangesDetail = styled.div`
  background: #f9f9f9;
  padding: 10px;
  border-radius: 4px;
  font-size: 13px;
`;

const ChangeRow = styled.div`
  display: flex;
  margin-bottom: 4px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const FieldName = styled.span`
  font-weight: 600;
  min-width: 150px;
  color: #444;
`;

const ChangeValue = styled.span`
  color: #666;
`;

const BeforeValue = styled.span`
  color: #d32f2f;
  margin-right: 8px;
`;

const AfterValue = styled.span`
  color: #388e3c;
`;

const isObjectId = (value) => {
  if (!value) return false;
  if (typeof value === "string" && /^[a-f\d]{24}$/i.test(value)) return true;
  if (typeof value === "object" && (value.$oid || value._bsontype)) return true;
  return false;
};

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/;

const formatValue = (value) => {
  if (value === null || value === undefined) return "N/A";
  if (isObjectId(value)) return null;
  if (typeof value === "string" && ISO_DATE_RE.test(value)) return formatTimestamp(value);
  if (typeof value === "object") {
    if (Array.isArray(value)) {
      return `Array (${value.length} items)`;
    }
    if (value.symbol && value.code) {
      return `${value.symbol} (${value.code})`;
    }
    if (value.name) {
      return value.name;
    }
    return JSON.stringify(value);
  }
  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }
  return String(value);
};

const formatFieldName = (field) => {
  return field
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (str) => str.toUpperCase())
    .trim();
};

const formatTimestamp = (ts) => {
  const d = new Date(ts);
  const date = d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  const time = d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  return `${date}, ${time}`;
};

const AuditLog = ({ brandId }) => {
  const [showLogs, setShowLogs] = useState(false);
  const { response: auditLogs, loading } = useAxios({
    callOnLoad: showLogs
      ? {
          method: "GET",
          url: `/brands/${brandId}/audit-logs`,
        }
      : null,
  });

  const logs = Array.isArray(auditLogs?.data?.data) ? auditLogs.data.data : [];

  return (
    <Container>
      <CollapsibleHeader
        header="Audit Log (Change History)"
        show={showLogs}
        setShow={setShowLogs}
      />
      {showLogs && (
        <>
          {loading ? (
            <div>Loading audit logs...</div>
          ) : logs.length === 0 ? (
            <Card>
              <p style={{ padding: "20px", textAlign: "center", color: "#666" }}>
                No audit logs found for this brand.
              </p>
            </Card>
          ) : (
            <Card>
              {logs.map((log) => {
                const visibleChanges = log.before
                  ? Object.entries(log.before).filter(([, values]) => {
                      if (isObjectId(values?.before) || isObjectId(values?.after)) return false;
                      const before = formatValue(values?.before);
                      const after = formatValue(values?.after);
                      return before !== null && after !== null && before !== after;
                    })
                  : [];

                // Entries without field-level diffs (e.g. item uploads) still
                // carry a description worth showing. Only skip entries that have
                // neither visible changes nor a description.
                if (visibleChanges.length === 0 && !log.description) return null;

                return (
                  <LogEntry key={log._id}>
                    <LogHeader>
                      <LogUser>{log.userName}</LogUser>
                      <LogTimestamp>{formatTimestamp(log.createdAt)}</LogTimestamp>
                    </LogHeader>
                    {visibleChanges.length === 0 ? (
                      <LogDescription>{log.description}</LogDescription>
                    ) : (
                      <>
                        <ChangedFields>
                          {visibleChanges.map(([field]) => (
                            <FieldTag key={field}>{formatFieldName(field)}</FieldTag>
                          ))}
                        </ChangedFields>
                        <ChangesDetail>
                          {visibleChanges.map(([field, values]) => (
                            <ChangeRow key={field}>
                              <FieldName>{formatFieldName(field)}:</FieldName>
                              <ChangeValue>
                                <BeforeValue>Before: {formatValue(values?.before)}</BeforeValue>
                                <AfterValue>After: {formatValue(values?.after)}</AfterValue>
                              </ChangeValue>
                            </ChangeRow>
                          ))}
                        </ChangesDetail>
                      </>
                    )}
                  </LogEntry>
                );
              })}
            </Card>
          )}
        </>
      )}
    </Container>
  );
};

export default AuditLog;
